/**
 * 拖放置入图片（DESIGN.md §2.6/§6.1 补充，Photoshop "置入嵌入对象" 的简化版）。
 *
 * 会话模型：drop 一张图 → 算一个"不超过画布、等比缩放后居中摆放"的初始
 * 矩阵 → 用户拖手柄调整大小（跟自由变换共用同一套手柄数学）→ Enter 提交
 * （栅格化进一个新图层，一条历史）→ Esc 取消（图片从没落进 TileStore/
 * 文档，丢弃会话即可，不留任何痕迹）。不支持旋转。
 *
 * 缩放默认等比（{@link uniform}，默认 true，跟 Photoshop 属性栏默认一致），
 * 取消勾选后角手柄自由拉伸、边手柄只影响那一个轴——跟自由变换
 * （transform-tool.ts）的 `uniform` 开关同一个模型，直接复用
 * `engine/transform/handles.ts` 的 `scaleByHandle`；勾选等比时角/边手柄
 * 都产出等比缩放，这一档单独用 `uniformScaleByHandle`（边手柄那种"沿边
 * 法线方向拖多远"的等比手感，`scaleByHandle` 的 `uniform` 选项只对角
 * 手柄生效，边手柄天生只影响一轴，两者不是同一件事，看各自的函数注释）。
 *
 * 跟自由变换（transform-tool.ts）的关键差异：那边操作的是**已经在文档里**
 * 的图层，会话期间要隐藏原图层、结束后把变换烘焙回同一个图层 id；这里
 * 图层压根还不存在——实时预览直接从解码出来的像素画（不用先写进
 * TileStore 再读出来），提交时才一次性写盘：先把原图按自然尺寸填进新图层
 * 的 tile，再用 `bakeLayerTransform` 把它变换到最终位置——两步写入共用
 * **同一个** PixelTransaction，且手动把两段各自会碰到的块都 `touch()`
 * 完再统一 `capture()`，否则第二步（烘焙）新碰到的块会漏在快照之外，
 * undo 时那些块恢复不到"写之前"（这里就是"什么都没有"）的状态。
 */
import type { Mat3, RasterLayer, Rect } from '../../document/types';
import { layerDefaults } from '../../document/draft';
import { applyFill } from '../../engine/fill/apply';
import { layerId, tileKey, tilesInRect, type LayerId } from '../../engine/tile/key';
import type { TileStore } from '../../engine/tile/store';
import {
  handlePoint,
  hitTestHandles,
  scaleByHandle,
  uniformScaleByHandle,
  type Point,
  type ScaleHandle,
} from '../../engine/transform/handles';
import { bakeLayerTransform, transformedBounds } from '../../engine/transform/bake';
import { IDENTITY, applyTo, multiply } from '../../engine/transform/mat3';
import { createAddLayerCommand } from '../../app/commands/layer-commands';
import type { AppStore } from '../../app/store';
import type { DecodedImage } from '../../io/codecs';
import { decodedImageToRgba8 } from '../../io/codecs';
import { SNAP_RELEASE_MULTIPLIER, SNAP_THRESHOLD_SCREEN_PX, drawSnapGuideLines, snapAxis } from './snap-guides';

interface Session {
  readonly layerId: LayerId;
  readonly displayName: string;
  readonly naturalWidth: number;
  readonly naturalHeight: number;
  readonly rgba: Uint8Array;
  readonly preview: HTMLCanvasElement | null;
  committed: Mat3;
  live: Mat3;
  drag: { readonly handle: ScaleHandle | 'move'; readonly from: Point } | null;
  /** 当前吸附命中的目标位置（文档坐标），只在拖"move"手柄（挪位置）
   * 时更新——拖角/边手柄调整大小不参与对齐吸附，见 snap-guides.ts。 */
  snapX: number | null;
  snapY: number | null;
}

export interface PlaceDeps {
  readonly store: AppStore;
  readonly tiles: TileStore;
  readonly nextLayerId: () => string;
  readonly setActiveLayer: (id: LayerId) => void;
  readonly requestRender: () => void;
  /** "视图→对齐"开关；缺省当作开启（跟 Photoshop 默认一致）。 */
  readonly snapEnabled?: () => boolean;
  /** 当前视口缩放，把屏幕像素的吸附阈值换算成文档像素；缺省当作 1。 */
  readonly viewportScale?: () => number;
}

export class PlaceToolController {
  readonly #deps: PlaceDeps;
  #session: Session | null = null;
  /**
   * commit() 提交期间（栅格化写盘是这个会话最慢的一步，大图能到一两秒）
   * 定格在最终位置继续画的预览——退出编辑框（手柄/属性栏）那一刻就该
   * 立刻切走，不用等这步写完，但完全不画东西的话，画面会有"图先凭空
   * 消失、写完才重新冒出来"的空窗，比手柄多留一下更违和，所以留一份
   * 静态快照顶上，直到真正的图层写进文档、`#session` 早就已经是 null。
   */
  #pendingPreview: { readonly preview: HTMLCanvasElement | null; readonly matrix: Mat3 } | null = null;
  /** 缩放是否等比，默认开——跟 transform-tool.ts 的 `#uniform` 同一个
   * 惯例：这是控制器级的开关，不随会话重置，切工具/开新会话都记得住
   * 用户上次选的模式。 */
  #uniform = true;

  constructor(deps: PlaceDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#session !== null;
  }

  get uniform(): boolean {
    return this.#uniform;
  }

  setUniform(value: boolean): void {
    this.#uniform = value;
  }

  /** 当前显示矩阵（committed ∘ live），同自由变换。 */
  get matrix(): Mat3 {
    const s = this.#session;
    return s === null ? IDENTITY : multiply(s.live, s.committed);
  }

  /** 当前显示宽/高（文档像素，四舍五入）——属性栏的宽/高栏读这个。 */
  get width(): number {
    const s = this.#session;
    return s === null ? 0 : Math.round(s.naturalWidth * this.matrix.a);
  }

  get height(): number {
    const s = this.#session;
    return s === null ? 0 : Math.round(s.naturalHeight * this.matrix.d);
  }

  /** 当前吸附命中的目标位置（文档坐标）；没有会话时为 null，某一轴
   * 没命中则该轴为 null。`drawOverlay` 据此画对齐提示线，测试也读它。 */
  get snapGuides(): { readonly x: number | null; readonly y: number | null } | null {
    const session = this.#session;
    return session === null ? null : { x: session.snapX, y: session.snapY };
  }

  /**
   * 开始一次置入会话：图片按不超过画布（不放大）的比例缩放后居中摆放。
   * 调用前必须已经有文档打开——没有文档时拖放导入走的是另一条"当作打开
   * 文件处理"的路径（见 app/drop-import.ts），不会走到这里。
   */
  begin(fileName: string, image: DecodedImage, docWidth: number, docHeight: number): void {
    const rgba = decodedImageToRgba8(image);
    const naturalWidth = image.width;
    const naturalHeight = image.height;
    // 不放大，只在图片比画布还大时缩小——跟真实 Photoshop 的默认行为一致
    const fitScale = Math.min(1, docWidth / naturalWidth, docHeight / naturalHeight);
    const displayWidth = naturalWidth * fitScale;
    const displayHeight = naturalHeight * fitScale;

    this.#session = {
      layerId: layerId(this.#deps.nextLayerId()),
      displayName: fileName.replace(/\.[^.]+$/, ''),
      naturalWidth,
      naturalHeight,
      rgba,
      preview: this.#buildPreview(rgba, naturalWidth, naturalHeight),
      committed: {
        a: fitScale,
        b: 0,
        c: 0,
        d: fitScale,
        e: (docWidth - displayWidth) / 2,
        f: (docHeight - displayHeight) / 2,
      },
      live: IDENTITY,
      drag: null,
      snapX: null,
      snapY: null,
    };
    this.#deps.requestRender();
  }

  #buildPreview(rgba: Uint8Array, width: number, height: number): HTMLCanvasElement | null {
    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return null;
    ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba), width, height), 0, 0);
    return canvas;
  }

  /** Esc：丢弃会话。图片从没写进 TileStore/文档，什么都不用清理。 */
  cancel(): void {
    if (this.#session === null) return;
    this.#session = null;
    this.#deps.requestRender();
  }

  /** 属性栏手动输入宽/高：等比时换算另一边，取消等比后只改这一栏对应的
   * 那个轴，另一轴维持当前显示尺寸不变；都围绕当前中心重新摆放。 */
  applySizeOption(key: 'width' | 'height', value: number): void {
    const session = this.#session;
    if (session === null || !Number.isFinite(value) || value <= 0) return;
    const displayed = this.#displayedBounds();
    const centerX = displayed.x + displayed.width / 2;
    const centerY = displayed.y + displayed.height / 2;
    const scaleForKey = key === 'width' ? value / session.naturalWidth : value / session.naturalHeight;
    const scaleX = key === 'width' || this.#uniform ? scaleForKey : displayed.width / session.naturalWidth;
    const scaleY = key === 'height' || this.#uniform ? scaleForKey : displayed.height / session.naturalHeight;
    const w = session.naturalWidth * scaleX;
    const h = session.naturalHeight * scaleY;
    session.committed = { a: scaleX, b: 0, c: 0, d: scaleY, e: centerX - w / 2, f: centerY - h / 2 };
    session.live = IDENTITY;
    session.drag = null;
    this.#deps.requestRender();
  }

  /**
   * Enter：栅格化提交，一条历史（新建图层 + 像素内容合成一条，undo 一步
   * 连图层一起撤掉，同 pasteFromClipboard 的惯例）。
   */
  async commit(): Promise<boolean> {
    const session = this.#session;
    const store = this.#deps.store;
    if (session === null) return false;
    const document = store.document;
    if (document === null) {
      this.cancel();
      return false;
    }

    const naturalBounds: Rect = { x: 0, y: 0, width: session.naturalWidth, height: session.naturalHeight };
    const finalMatrix = this.matrix;
    const target = transformedBounds(naturalBounds, finalMatrix, document.size.width, document.size.height);
    if (target.width <= 0 || target.height <= 0) {
      // 缩/挪到画布外面去了，没什么可提交的——比悄悄新建一个空图层更诚实
      this.cancel();
      return false;
    }

    const layer: RasterLayer = {
      ...layerDefaults(session.layerId, session.displayName),
      kind: 'raster',
      tiles: this.#deps.tiles.handle(session.layerId),
      bounds: { x: 0, y: 0, width: 0, height: 0 },
    };

    const label = `置入「${session.displayName}」`;
    const transaction = store.beginPixelTransaction(label);
    // 两步写入（先填自然尺寸原图，再烘焙到最终位置）各自会碰到不同的块，
    // 必须先把两边的块都 touch 完再统一 capture 一次——否则第二步新碰到
    // 的块的"写之前"快照就漏了，undo 会把那些块恢复到错误的状态（本该是
    // "什么都没有"，而不是烘焙前那一刻碰巧写进去的原图内容）
    for (const { tx, ty } of tilesInRect(naturalBounds.x, naturalBounds.y, naturalBounds.width, naturalBounds.height)) {
      transaction.touch(tileKey(session.layerId, tx, ty));
    }
    for (const { tx, ty } of tilesInRect(target.x, target.y, target.width, target.height)) {
      transaction.touch(tileKey(session.layerId, tx, ty));
    }
    transaction.capture();

    applyFill(
      this.#deps.tiles,
      session.layerId,
      naturalBounds,
      (x, y) => {
        const at = (y * session.naturalWidth + x) * 4;
        return [session.rgba[at]!, session.rgba[at + 1]!, session.rgba[at + 2]!, session.rgba[at + 3]!];
      },
      transaction,
    );

    // 退出编辑框（手柄消失、属性栏切回去、指针交回其它工具）不用等下面
    // 这步栅格化写盘跑完——那是这整个 commit 里最慢的一步，回车之后应该
    // 立刻感觉"退出了"，写盘留在背后继续（`session` 是本地捕获的快照，
    // 跟 `this.#session` 无关，后面的异步代码照样能用）。main.tsx 紧跟着
    // 调 endPlace() 把工具切回去，这里同步清掉 `this.#session` 正是让那
    // 一步立刻生效的关键——async 函数体在第一个 await 之前都是同步执行，
    // 调用方拿到 Promise 之前这几行已经跑完了。
    this.#pendingPreview = { preview: session.preview, matrix: finalMatrix };
    this.#session = null;
    this.#deps.requestRender();

    await bakeLayerTransform(
      this.#deps.tiles,
      session.layerId,
      naturalBounds,
      finalMatrix,
      document.size.width,
      document.size.height,
      transaction,
    );

    store.dispatchWithPixels(
      createAddLayerCommand({ layer, parentId: null, index: document.root.children.length }),
      transaction.end(),
    );
    store.invalidatePixels([
      ...tilesInRect(naturalBounds.x, naturalBounds.y, naturalBounds.width, naturalBounds.height),
      ...tilesInRect(target.x, target.y, target.width, target.height),
    ]);
    this.#deps.setActiveLayer(session.layerId);
    this.#pendingPreview = null;
    this.#deps.requestRender();
    return true;
  }

  /** 指针路由：会话激活时接管画布指针，同自由变换的 tolerance 换算方式。 */
  handlePointer(phase: 'down' | 'move' | 'up' | 'double', point: Point, tolerance: number): boolean {
    const session = this.#session;
    if (session === null) return false;

    if (phase === 'down') {
      const displayed = this.#displayedBounds();
      const rawHandle = hitTestHandles(displayed, point, tolerance);
      // 不支持旋转：命中手柄圈外的旋转热区时当"点在空处"处理
      const handle = rawHandle === 'rotate' ? null : rawHandle;
      const inside =
        point.x >= displayed.x &&
        point.x <= displayed.x + displayed.width &&
        point.y >= displayed.y &&
        point.y <= displayed.y + displayed.height;
      if (handle === null && !inside) return true; // 点在空处：吞掉但不动
      session.drag = { handle: handle ?? 'move', from: point };
      return true;
    }

    if (phase === 'move' && session.drag !== null) {
      const { handle, from } = session.drag;
      if (handle === 'move') {
        let dx = point.x - from.x;
        let dy = point.y - from.y;
        if (this.#deps.snapEnabled?.() !== false) {
          [dx, dy] = this.#snap(session, dx, dy);
        } else {
          session.snapX = null;
          session.snapY = null;
        }
        session.live = { a: 1, b: 0, c: 0, d: 1, e: dx, f: dy };
      } else {
        // 调整大小不参与对齐吸附，把上一次拖"挪位置"留下的提示线清掉
        session.snapX = null;
        session.snapY = null;
        const displayed = this.#displayedBounds();
        // 等比时角/边手柄都要产出等比缩放（uniformScaleByHandle 的"锚点
        // 到手柄距离比"手感），不能用 scaleByHandle 的 uniform 选项代替——
        // 那个选项只在角手柄上生效，边手柄天生只影响一轴，见文件头注释
        session.live = this.#uniform
          ? uniformScaleByHandle(displayed, handle, point)
          : scaleByHandle(displayed, handle, point);
      }
      this.#deps.requestRender();
      return true;
    }

    if (phase === 'up' && session.drag !== null) {
      session.committed = multiply(session.live, session.committed);
      session.live = IDENTITY;
      session.drag = null;
      // 提示线只在拖拽过程中有意义，松手后不该继续留在画面上
      session.snapX = null;
      session.snapY = null;
      this.#deps.requestRender();
      return true;
    }
    return true;
  }

  /**
   * 把"挪位置"拖拽的位移吸附到画布边缘/中心与参考线（同移动工具，见
   * snap-guides.ts）。用 `committed`（不含这次拖拽还在进行中的 `live`）
   * 算内容边界——`from` 是拖拽开始那一刻的点，dx/dy 每次都是"相对拖拽
   * 起点的总位移"而不是增量，边界必须固定在拖拽开始时的位置，否则会
   * 把上一次吸附结果也算进偏移里，越吸越偏。
   */
  #snap(session: Session, dx: number, dy: number): [number, number] {
    const document = this.#deps.store.document;
    if (document === null) {
      session.snapX = null;
      session.snapY = null;
      return [dx, dy];
    }
    const scale = this.#deps.viewportScale?.() ?? 1;
    const threshold = SNAP_THRESHOLD_SCREEN_PX / scale;
    const releaseThreshold = (SNAP_THRESHOLD_SCREEN_PX * SNAP_RELEASE_MULTIPLIER) / scale;
    const bounds = this.#boundsForMatrix(session, session.committed);

    const guideX: number[] = [];
    const guideY: number[] = [];
    for (const guide of document.guides) {
      if (guide.axis === 'vertical') guideX.push(guide.position);
      else guideY.push(guide.position);
    }

    const snappedX = snapAxis({
      raw: dx,
      boundsStart: bounds.x,
      boundsSize: bounds.width,
      docSize: document.size.width,
      guidePositions: guideX,
      threshold,
      releaseThreshold,
      stickyTarget: session.snapX,
    });
    const snappedY = snapAxis({
      raw: dy,
      boundsStart: bounds.y,
      boundsSize: bounds.height,
      docSize: document.size.height,
      guidePositions: guideY,
      threshold,
      releaseThreshold,
      stickyTarget: session.snapY,
    });

    session.snapX = snappedX.target;
    session.snapY = snappedY.target;
    return [snappedX.value, snappedY.value];
  }

  /** 文档空间叠加：预览 + 边框 + 八个手柄（视觉同裁剪工具，但都只产出等比缩放）。 */
  drawOverlay(ctx: CanvasRenderingContext2D, viewportScale: number): void {
    const session = this.#session;
    if (session === null) {
      // 会话已经退出（commit 正在背后栅格化写盘），只画定格预览，不画
      // 边框/手柄——那些跟着会话一起立刻消失才是这次要的效果
      const pending = this.#pendingPreview;
      if (pending !== null && pending.preview !== null) {
        ctx.save();
        ctx.transform(pending.matrix.a, pending.matrix.b, pending.matrix.c, pending.matrix.d, pending.matrix.e, pending.matrix.f);
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(pending.preview, 0, 0);
        ctx.restore();
      }
      return;
    }
    const m = this.matrix;

    if (session.preview !== null) {
      ctx.save();
      ctx.transform(m.a, m.b, m.c, m.d, m.e, m.f);
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(session.preview, 0, 0);
      ctx.restore();
    }

    const displayed = this.#displayedBounds();
    ctx.save();
    ctx.strokeStyle = 'rgba(64,128,255,0.9)';
    ctx.lineWidth = 1 / viewportScale;
    ctx.strokeRect(displayed.x, displayed.y, displayed.width, displayed.height);

    const size = 6 / viewportScale;
    ctx.fillStyle = 'white';
    const handles: readonly ScaleHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
    for (const handle of handles) {
      const p = handlePoint(displayed, handle);
      ctx.strokeRect(p.x - size / 2, p.y - size / 2, size, size);
      ctx.fillRect(p.x - size / 2, p.y - size / 2, size, size);
    }
    ctx.restore();

    const docSize = this.#deps.store.document?.size;
    if (docSize !== undefined) {
      drawSnapGuideLines(ctx, session.snapX, session.snapY, docSize, viewportScale);
    }
  }

  /** 当前显示的外接矩形（自然尺寸经 committed∘live），不裁到画布内——
   *  拖手柄拖出画布边缘时手柄本身仍要能看见、能继续拖回来。 */
  #displayedBounds(): Rect {
    return this.#boundsForMatrix(this.#session!, this.matrix);
  }

  /** 自然尺寸经给定矩阵变换后的外接矩形——`#displayedBounds` 传
   * `this.matrix`（含正在拖拽的 `live`），`#snap` 传 `session.committed`
   * （拖拽开始前那一刻，不含这次还在进行中的位移），两处共用同一份算法。 */
  #boundsForMatrix(session: Session, m: Mat3): Rect {
    const corners = [
      applyTo(m, { x: 0, y: 0 }),
      applyTo(m, { x: session.naturalWidth, y: 0 }),
      applyTo(m, { x: session.naturalWidth, y: session.naturalHeight }),
      applyTo(m, { x: 0, y: session.naturalHeight }),
    ];
    const xs = corners.map((c) => c.x);
    const ys = corners.map((c) => c.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return {
      x: minX,
      y: minY,
      width: Math.max(...xs) - minX,
      height: Math.max(...ys) - minY,
    };
  }
}
